import React, { useEffect, useState, useRef } from "react";
import api from "../../api/api";
import "../../css/Employee.css";

const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [employeesPerPage, setEmployeesPerPage] = useState(10);
  const [showListFilter, setShowListFilter] = useState(false);

  const listFilterRef = useRef(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    password: "",
    company_name: "",
    company_id: null,
    state: "", // ✅ Added
    state_id: null, // ✅ Added
  });

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("all");

  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("all");

  // Fetch employees
  useEffect(() => {
    api
      .get("/employee/list")
      .then((response) => {
        if (response.data.success) {
          setEmployees(response.data.data);
          setFilteredEmployees(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching employees:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Fetch companies
  useEffect(() => {
    api
      .get("/get/company")
      .then((response) => {
        if (response.data.success) {
          setCompanies(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching companies:", error);
      });
  }, []);

  // Fetch states
  useEffect(() => {
    api
      .get("/get/state")
      .then((response) => {
        if (response.data.success) {
          setStates(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching states:", error);
      });
  }, []);

  // Filtering logic
  useEffect(() => {
    let filtered = employees;

    if (selectedCompany !== "all") {
      filtered = filtered.filter((emp) => emp.company_name === selectedCompany);
    }

    if (selectedState !== "all") {
      filtered = filtered.filter((emp) => emp.state === selectedState);
    }

    filtered = filtered.filter(
      (emp) =>
        `${emp.firstname || ""} ${emp.lastname || ""}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (emp.username || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.phone || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.company_name || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (emp.department_name || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (emp.designation_name || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (emp.state || "").toLowerCase().includes(searchTerm.toLowerCase())
    );

    setFilteredEmployees(filtered);
    setCurrentPage(1);
  }, [searchTerm, employees, selectedCompany, selectedState]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        listFilterRef.current &&
        !listFilterRef.current.contains(event.target)
      ) {
        setShowListFilter(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handlePerPageChange = (value) => {
    setEmployeesPerPage(value);
    setShowListFilter(false);
    setCurrentPage(1);
  };

  const indexOfLastEmployee = currentPage * employeesPerPage;
  const indexOfFirstEmployee = indexOfLastEmployee - employeesPerPage;
  const currentEmployees = filteredEmployees.slice(
    indexOfFirstEmployee,
    indexOfLastEmployee
  );
  const totalPages = Math.ceil(filteredEmployees.length / employeesPerPage);

  const handleView = (emp) => {
    alert(`View Employee ID: ${emp.emp_id}`);
  };

  const handleEdit = (emp) => {
    setSelectedEmployee(emp);
    setFormData({
      firstname: emp.firstname || "",
      lastname: emp.lastname || "",
      email: emp.email || "",
      phone: emp.phone || "",
      password: "",
      company_name: emp.company_name || "",
      company_id: emp.company_id || null,
      state: emp.state || "", // ✅
      state_id: emp.state_id || null, // ✅
    });
    setShowEditModal(true);
  };

  const handleDelete = (emp) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${emp.firstname} ${emp.lastname}?`
    );
    if (confirmDelete) {
      api
        .post("/delete/employee", { id: emp.emp_id })
        .then((response) => {
          if (response.data.success) {
            alert("Employee deleted successfully!");
            const updatedEmployees = employees.filter(
              (e) => e.emp_id !== emp.emp_id
            );
            setEmployees(updatedEmployees);
            setFilteredEmployees(updatedEmployees);
          } else {
            alert("Failed to delete employee. Please try again.");
          }
        })
        .catch((error) => {
          console.error("Error deleting employee:", error);
          alert("An error occurred while deleting the employee.");
        });
    }
  };

  const handleUpdate = () => {
    if (!formData.password.trim()) {
      alert("Password is required!");
      return;
    }

    const payload = {
      id: selectedEmployee.emp_id,
      company_id: formData.company_id || selectedEmployee.company_id,
      state_id: formData.state_id || selectedEmployee.state_id, // ✅
      ...formData,
    };

    api
      .post("/update/employee/data", payload)
      .then((response) => {
        if (response.data.status) {
          alert("Employee updated successfully!");
          const updatedEmployees = employees.map((e) =>
            e.emp_id === selectedEmployee.emp_id ? { ...e, ...formData } : e
          );
          setEmployees(updatedEmployees);
          setFilteredEmployees(updatedEmployees);
          setShowEditModal(false);
          setSelectedEmployee(null);
          setFormData({
            firstname: "",
            lastname: "",
            email: "",
            phone: "",
            password: "",
            company_name: "",
            company_id: null,
            state: "", // ✅
            state_id: null, // ✅
          });
        } else {
          alert("Failed to update employee. Please try again.");
        }
      })
      .catch((error) => {
        console.error("Error updating employee:", error);
        alert("An error occurred while updating the employee.");
      });
  };

  return (
    <section className="employee-section">
      <div className="inner-content">
        <div className="filter-wrapper">
          <h2>Employee List</h2>
          <div className="right-filter">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div
              className="select-company tooltip-wrapper"
              data-tooltip="Filter by Company"
            >
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
              >
                <option value="all">All Companies</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.name}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
            <div
              className="state-select tooltip-wrapper"
              data-tooltip="Filter by State"
            >
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
              >
                <option value="all">All States</option>
                {states.map((state) => (
                  <option key={state.state_id} value={state.state_title}>
                    {state.state_title}
                  </option>
                ))}
              </select>
            </div>

            <div
              className="list-fliter  tooltip-wrapper"
              ref={listFilterRef}
              data-tooltip="Filter by List"
            >
              <button onClick={() => setShowListFilter(!showListFilter)}>
                <i className="fa-solid fa-filter"></i>
              </button>
              {showListFilter && (
                <div className="filter-data">
                  {[10, 30, 50, 100].map((number) => (
                    <div
                      key={number}
                      className={`filter-option ${
                        employeesPerPage === number ? "selected" : ""
                      }`}
                      onClick={() => handlePerPageChange(number)}
                    >
                      {number}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            <div className="table-wrapper">
              <table className="employee-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Full Name</th>
                    <th>Phone</th>
                    <th>Company</th>
                    <th>Department</th>
                    <th>Designation</th>
                    <th>State</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentEmployees.map((emp, index) => (
                    <tr key={emp.emp_id}>
                      <td>{indexOfFirstEmployee + index + 1}</td>
                      <td>
                        {emp.firstname} {emp.lastname}
                      </td>
                      <td className="phone">
                        <i class="fa-solid fa-phone"></i>&nbsp; {emp.phone}
                      </td>
                      <td className="p-day">{emp.company_name}</td>
                      <td>{emp.department_name}</td>
                      <td>{emp.designation_name}</td>
                      <td className="state">{emp.state}</td>
                      <td>
                        <div className="auction-btns">
                          <button
                            onClick={() => handleView(emp)}
                            className="action-btn view"
                          >
                            <i className="fa-regular fa-eye"></i>
                          </button>
                          <button
                            onClick={() => handleEdit(emp)}
                            className="action-btn edit"
                          >
                            <i className="fa-solid fa-pen-to-square"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(emp)}
                            className="action-btn delete"
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Prev
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <div className="modal-overlay">
            <div className="modal">
              <h3>Edit Employee</h3>
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  value={formData.firstname}
                  onChange={(e) =>
                    setFormData({ ...formData, firstname: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={formData.lastname}
                  onChange={(e) =>
                    setFormData({ ...formData, lastname: e.target.value })
                  }
                />
              </div>
              <div className="form-group select-company">
                <label>Select Company</label>
                <select
                  value={formData.company_name}
                  onChange={(e) => {
                    const selected = companies.find(
                      (c) => c.name === e.target.value
                    );
                    setFormData({
                      ...formData,
                      company_name: e.target.value,
                      company_id: selected ? selected.id : null,
                    });
                  }}
                >
                  <option value="">-- Select Company --</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.name}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* ✅ State Dropdown Added */}
              <div className="form-group select-state">
                <label>Select State</label>
                <select
                  value={formData.state}
                  onChange={(e) => {
                    const selected = states.find(
                      (s) => s.state_title === e.target.value
                    );
                    setFormData({
                      ...formData,
                      state: e.target.value,
                      state_id: selected ? selected.state_id : null,
                    });
                  }}
                >
                  <option value="">-- Select State --</option>
                  {states.map((state) => (
                    <option key={state.state_id} value={state.state_title}>
                      {state.state_title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                />
              </div>
              <div className="modal-actions">
                <button
                  className="close-btn"
                  onClick={() => setShowEditModal(false)}
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
                <button className="update-btn" onClick={handleUpdate}>
                  Update
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default EmployeeList;
